import { type SelectHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  error?: string;
};

const BASE = cn(
  'w-full cursor-pointer appearance-none rounded-[10px] border bg-surface',
  'h-[34px] px-3 pr-9 text-xs text-text-primary',
  'transition-colors duration-200 hover:border-border-default',
  'focus:outline-none focus:ring-2 focus:ring-brand-red/30'
);

const CHEVRON =
  'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary';
const LABEL = 'mb-1.5 block text-xs font-medium text-text-secondary';

function FieldLabel(p: {
  id: string | undefined;
  text: string;
  required: boolean;
}): ReactNode {
  return (
    <label htmlFor={p.id} className={LABEL}>
      {p.text}
      {p.required && <span className="ml-0.5 text-brand-red">*</span>}
    </label>
  );
}

function selectClass(error?: string): string {
  const border = error
    ? 'border-error'
    : 'border-border-subtle focus:border-brand-red';
  return cn(BASE, border);
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { label, error, className, id, children, required, ...props },
    ref
  ) {
    const sid = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div>
        {label && <FieldLabel id={sid} text={label} required={!!required} />}
        <div className="relative">
          <select
            ref={ref}
            id={sid}
            required={required}
            className={cn(selectClass(error), className)}
            {...props}
          >
            {children}
          </select>
          <ChevronDown size={14} className={CHEVRON} />
        </div>
        {error && <p className="mt-1 text-xs text-error">{error}</p>}
      </div>
    );
  }
);
